import { useTranslations } from "next-intl";
import { css } from "styled-system/css";
import { flex, stack } from "styled-system/patterns";

import { DefaultError } from "@/components/handle-query";
import { Skeleton } from "@/components/skeleton";
import type { useReferralFunnelQuery } from "@/contexts/queries/referral-funnel";

import { SectionCard } from "../../components";
import { formatDayLabel } from "../../points/derive";
import { toFunnelBars } from "../funnel";

const WINDOW_DAYS = 14;

/**
 * Sign-ups against first-time deposits per UTC day — the acquisition funnel.
 *
 * Two series, so a legend is mandatory: identity must never rest on colour alone.
 * The pair is lilac vs brand lime, which clears colourblind separation by a wide
 * margin (ΔE 30.7 deutan, 15.9 tritan against an 8 target) and both clear 3:1 on the
 * card surface.
 *
 * Sign-ups are code redemptions, which is the live signal. Deposits count each
 * referral's *first* collateral deposit, so a referral appears in that series at most
 * once ever — the two series are not a ratio and the deposit bar can lead on a day
 * when older referrals fund.
 */
export function FunnelChart({ query }: { query: ReturnType<typeof useReferralFunnelQuery> }) {
  const t = useTranslations("affiliates.overview.funnelChart");

  if (query.isError) {
    return (
      <SectionCard label={t("window")} title={t("title")} fill>
        <DefaultError error={query.error} resetErrorBoundary={() => void query.refetch()} />
      </SectionCard>
    );
  }

  if (query.data === undefined) {
    return (
      <SectionCard label={t("window")} title={t("title")} fill>
        <Skeleton className={css({ w: "full", h: "[160px]", rounded: "200" })} />
      </SectionCard>
    );
  }

  const bars = toFunnelBars(query.data, WINDOW_DAYS);

  if (bars.length === 0) {
    return (
      <SectionCard label={t("window")} title={t("title")} fill>
        <div className={css({ textStyle: "body.md", color: "text.tertiary" })}>{t("empty")}</div>
      </SectionCard>
    );
  }

  const totalSignUps = bars.reduce((sum, bar) => sum + bar.signUps, 0);
  // One scale across both series: separate scales would invent a correlation.
  // Floored at 1 so an all-zero window renders flat instead of dividing by zero.
  const max = Math.max(...bars.flatMap((bar) => [bar.signUps, bar.firstDeposits]), 1);

  return (
    <SectionCard
      label={t("window")}
      title={t("title")}
      rightSlot={
        <span className={css({ textStyle: "body.md", color: "text.tertiary" })}>
          {t("total", { count: totalSignUps.toLocaleString("en-US") })}
        </span>
      }
      fill
    >
      <div className={stack({ gap: "12", minW: "0", flex: "1" })}>
        <div className={flex({ gap: "16", alignItems: "center", flexWrap: "wrap" })}>
          <LegendKey color="lilac.100" label={t("signUps")} />
          <LegendKey color="brandYellow.550" label={t("firstDeposits")} />
        </div>

        <div className={chartAreaStyles}>
          {bars.map((bar) => (
            <div
              key={bar.dayStartUnixMs}
              className={dayCellStyles}
              title={t("barTooltip", {
                label: formatDayLabel(bar.dayStartUnixMs),
                signUps: bar.signUps,
                deposits: bar.firstDeposits,
              })}
            >
              <div
                className={css({ ...barBase, bg: "lilac.100" })}
                style={{ height: `${(bar.signUps / max) * 100}%` }}
              />
              <div
                className={css({ ...barBase, bg: "brandYellow.550" })}
                style={{ height: `${(bar.firstDeposits / max) * 100}%` }}
              />
            </div>
          ))}
        </div>

        <div className={flex({ justifyContent: "space-between", gap: "8" })}>
          <span className={axisLabelStyles}>{formatDayLabel(bars[0]?.dayStartUnixMs ?? 0)}</span>
          {bars.length > 1 && (
            <span className={axisLabelStyles}>
              {formatDayLabel(bars[bars.length - 1]?.dayStartUnixMs ?? 0)}
            </span>
          )}
        </div>
      </div>
    </SectionCard>
  );
}

/**
 * Colour key beside the label — never the label itself in the series colour, since a
 * pale lilac or lime is illegible as text.
 *
 * The token is a literal union rather than `string`: Panda only accepts known colour
 * tokens, so this keeps a typo a compile error instead of a silently dropped style.
 */
function LegendKey({ color, label }: { color: SeriesColor; label: string }) {
  return (
    <span className={flex({ gap: "8", alignItems: "center" })}>
      <span className={css({ w: "8", h: "8", rounded: "full", bg: color })} />
      <span className={css({ textStyle: "body.md", color: "text.secondary" })}>{label}</span>
    </span>
  );
}

type SeriesColor = "lilac.100" | "brandYellow.550";

const chartAreaStyles = flex({
  alignItems: "flex-end",
  gap: "4",
  flex: "1",
  minH: "[120px]",
  w: "full",
});

/** A day slot holding both series' bars, separated by a 2px surface gap. */
const dayCellStyles = flex({
  flex: "1",
  gap: "2",
  alignItems: "flex-end",
  minW: "0",
  h: "full",
});

const barBase = {
  flex: "1",
  minW: "0",
  // Rounded cap, square at the baseline. minHeight keeps a zero day on the axis.
  roundedTop: "200",
  minH: "[2px]",
} as const;

const axisLabelStyles = css({
  textStyle: "body.sm",
  color: "text.tertiary",
});
